import { Button } from '../ui/button';
import {
  Plus, Trash2, Target, AlertTriangle, Sparkles,
  ChevronDown, ChevronRight, Link2, GripVertical, Paperclip, XCircle
} from 'lucide-react';

export function FishboneTableView({
  diagram, canEdit, expandedCats, setExpandedCats,
  handleDragStart, handleDrop, handleAiSuggest, aiLoading,
  setAddCauseDialog, setCauseText, handleDeleteCategory,
  handleToggleRootCause, handleToggleContributing,
  openEvidenceDialog, setAddSubDialog, setSubText,
  handleDeleteCause, handleDeleteSubCause, setAddCategoryDialog
}) {
  return (
    <div className="mb-6 space-y-2" data-testid="fishbone-table-view">
      {(diagram.categories || []).map(cat => (
        <div key={cat.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50"
            draggable={canEdit}
            onDragStart={() => handleDragStart('category', null, cat.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(null, (diagram.categories || []).indexOf(cat))}
            onClick={() => setExpandedCats(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}>
            <div className="flex items-center gap-2">
              {canEdit && <GripVertical className="w-3 h-3 text-slate-300 cursor-grab" />}
              {expandedCats[cat.id] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
              <span className="font-semibold text-sm text-slate-800">{cat.name}</span>
              <span className="text-[10px] text-slate-400">{(cat.causes || []).length}</span>
            </div>
            <div className="flex items-center gap-1">
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-purple-600"
                    onClick={(e) => { e.stopPropagation(); handleAiSuggest(cat.name); }}
                    disabled={aiLoading} data-testid={`ai-suggest-${cat.id}`}>
                    <Sparkles className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-blue-600"
                    onClick={(e) => { e.stopPropagation(); setAddCauseDialog({ catId: cat.id, catName: cat.name }); setCauseText(''); }}
                    data-testid={`add-cause-${cat.id}`}>
                    <Plus className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                    data-testid={`delete-cat-${cat.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {expandedCats[cat.id] && (
            <div className="px-4 py-2">
              {(cat.causes || []).length === 0 ? (
                <div className="py-2 text-center text-xs text-slate-400">No causes identified yet</div>
              ) : (
                <ul className="space-y-1">
                  {(cat.causes || []).map(cause => (
                    <li key={cause.id}
                      draggable={canEdit}
                      onDragStart={() => handleDragStart('cause', cat.id, cause.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(cat.id, (cat.causes || []).indexOf(cause))}>
                      <div className="flex items-center gap-2 group py-0.5">
                        {cause.is_root_cause ? (
                          <span className="shrink-0 w-2 h-2 rounded-full bg-red-500" title="Root Cause" />
                        ) : cause.is_contributing_factor ? (
                          <span className="shrink-0 w-2 h-2 rounded-full bg-amber-500" title="Contributing Factor" />
                        ) : (
                          <span className="shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
                        )}
                        <span className={`text-xs flex-1 min-w-0 truncate ${cause.is_root_cause ? 'text-red-700 font-medium' : cause.is_contributing_factor ? 'text-amber-700 font-medium' : 'text-slate-700'}`}>
                          {cause.text}
                        </span>
                        {cause.evidence_ids?.length > 0 && (
                          <span className="shrink-0 text-[9px] text-blue-500"><Link2 className="w-2.5 h-2.5 inline" /></span>
                        )}
                        {(cause.sub_causes || []).length > 0 && (
                          <span className="shrink-0 text-[9px] text-slate-400">{cause.sub_causes.length}s</span>
                        )}
                        {canEdit && (
                          <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleToggleRootCause(cause.id, cause.is_root_cause)}
                              className={`p-0.5 rounded ${cause.is_root_cause ? 'text-red-500' : 'text-slate-300 hover:text-red-500'}`}
                              title="Root Cause" data-testid={`toggle-root-${cause.id}`}>
                              <Target className="w-3 h-3" />
                            </button>
                            <button onClick={() => handleToggleContributing(cause.id, cause.is_contributing_factor)}
                              className={`p-0.5 rounded ${cause.is_contributing_factor ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
                              title="Contributing" data-testid={`toggle-contrib-${cause.id}`}>
                              <AlertTriangle className="w-3 h-3" />
                            </button>
                            <button onClick={() => openEvidenceDialog(cause.id, cause.evidence_ids || [])}
                              className="p-0.5 rounded text-slate-300 hover:text-blue-500"
                              title="Evidence" data-testid={`link-evidence-${cause.id}`}>
                              <Paperclip className="w-3 h-3" />
                            </button>
                            <button onClick={() => { setAddSubDialog({ catId: cat.id, causeId: cause.id, causeText: cause.text }); setSubText(''); }}
                              className="p-0.5 rounded text-slate-300 hover:text-blue-500" title="Sub-cause">
                              <Plus className="w-3 h-3" />
                            </button>
                            <button onClick={() => handleDeleteCause(cat.id, cause.id)}
                              className="p-0.5 rounded text-slate-300 hover:text-red-500" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      {(cause.sub_causes || []).length > 0 && (
                        <ul className="ml-5 space-y-0.5 mt-0.5">
                          {cause.sub_causes.map(sub => (
                            <li key={sub.id} className="flex items-center gap-1.5 group/sub">
                              <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
                              <span className="text-[11px] text-slate-500 flex-1 truncate">{sub.text}</span>
                              {canEdit && (
                                <button onClick={() => handleDeleteSubCause(cause.id, sub.id)}
                                  className="p-0.5 text-slate-200 hover:text-red-500 opacity-0 group-hover/sub:opacity-100 transition-opacity">
                                  <XCircle className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ))}

      {canEdit && (
        <button onClick={() => setAddCategoryDialog(true)}
          className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-xl text-xs text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
          data-testid="add-category-btn">
          <Plus className="w-3.5 h-3.5 inline mr-1" /> Add Category
        </button>
      )}
    </div>
  );
}
